import React from 'react';
import { Drawer, Form, InputNumber, Input, Button, Space, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import {
  CREATE_USPS_ADDRESS_PROCESS,
  CreateUspsAddressProcessInput,
} from '../graphql/uspsAddressProcessGraphql';

interface AddUspsAddressProcessDrawerProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormValues {
  orderAddressId: number;
  originalAddress: string;
}

const AddUspsAddressProcessDrawer: React.FC<AddUspsAddressProcessDrawerProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();

  const [createUsps, { loading }] = useMutation(CREATE_USPS_ADDRESS_PROCESS, {
    onCompleted: () => {
      message.success(t('mapTool.uspsAddressProcess.add.successMsg') as string);
      form.resetFields();
      onClose();
      onSuccess();
    },
    onError: (err) => message.error(err.message),
  });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const input: CreateUspsAddressProcessInput = {
        orderAddressId: values.orderAddressId,
        originalAddress: values.originalAddress.trim(),
      };
      createUsps({ variables: { input } });
    } catch {
      // validateFields 会自动展示字段错误
    }
  };

  return (
    <Drawer
      title={t('mapTool.uspsAddressProcess.add.drawerTitle')}
      open={visible}
      onClose={onClose}
      width={520}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={onClose}>{t('components.button.cancel')}</Button>
          <Button type="primary" loading={loading} onClick={handleSubmit}>
            {t('components.button.confirm')}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="orderAddressId"
          label={t('mapTool.uspsAddressProcess.add.orderAddressId')}
          rules={[
            { required: true, message: t('mapTool.uspsAddressProcess.add.orderAddressIdRequired') as string },
            {
              validator: (_, v) =>
                v > 0 ? Promise.resolve() : Promise.reject(new Error(t('mapTool.uspsAddressProcess.add.orderAddressIdPositive') as string)),
            },
          ]}
        >
          <InputNumber
            className="w-full"
            min={1}
            step={1}
            placeholder={t('mapTool.uspsAddressProcess.add.orderAddressIdPlaceholder') as string}
          />
        </Form.Item>
        <Form.Item
          name="originalAddress"
          label={t('mapTool.uspsAddressProcess.add.originalAddress')}
          rules={[
            { required: true, message: t('mapTool.uspsAddressProcess.add.originalAddressRequired') as string },
            { max: 255, message: t('mapTool.uspsAddressProcess.add.originalAddressMax') as string },
          ]}
        >
          <Input.TextArea
            rows={3}
            maxLength={255}
            showCount
            placeholder={t('mapTool.uspsAddressProcess.add.originalAddressPlaceholder') as string}
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default AddUspsAddressProcessDrawer;
